/* eslint-disable no-restricted-properties */
import moment, { Moment } from 'moment';
import uniqBy from 'lodash/uniqBy';
import isEmpty from 'lodash/isEmpty';

import {
  AttachmentTypeCategoryDto,
  AttachmentTypeDto,
  BaseAttachmentDto,
  BaseFileTypeCategoryDto,
  BaseFileTypeDto,
  CandidateFileDto,
  ImageDto,
  PrivateFileDto,
  PrivateFileTypeCategoryDto,
  PrivateFileTypeDto,
  ResumeDto,
} from '@eva/types/dto';
import { attachmentTypeCodes } from 'shared/constants';
import { momentify, stringifyError } from 'shared/functions';
// eslint-disable-next-line import/no-cycle
import { buildOptionFromFileType, FormUploadFileValues } from 'shared/ui/Modal/ModalUploadFile';
import { FileButtonEditFile } from 'shared/ui/FileButtonEditFile';
import { FileButtonParse } from 'shared/ui/FileButtonParse';
import { UploadFileBaseValues } from 'shared/ui/Modal/types';

import { IsAllowedOperation } from 'types/generic-context';
import { ControlRenderProps } from 'components/GroupedList';
import { getDeleteEntityControlRenderer } from 'containers/GroupedTable';

import type { FileOperations, FilesFilterValues, FormValuesType } from './types';

import FileButtonDownload from './FilesList/FileButtonDownload';

export const isPrivateFile = (obj: Partial<PrivateFileDto | unknown>): obj is PrivateFileDto =>
  !!(obj as PrivateFileDto)?.privateFileId;

const isPrivateFileType = (obj: Partial<PrivateFileTypeDto | unknown>): obj is PrivateFileTypeDto =>
  !!(obj as PrivateFileTypeDto)?.privateFileTypeId;

const isPrivateFileTypeCategory = (
  obj: Partial<PrivateFileTypeCategoryDto | unknown>,
): obj is PrivateFileTypeCategoryDto => !!(obj as PrivateFileTypeCategoryDto)?.privateFileTypeCategoryId;

export const isCandidateFile = (obj: Partial<CandidateFileDto | unknown>): obj is CandidateFileDto =>
  !!(obj as CandidateFileDto)?.attachmentId;

const isAttachmentType = (obj: Partial<AttachmentTypeDto | unknown>): obj is AttachmentTypeDto =>
  !!(obj as AttachmentTypeDto)?.attachmentTypeId;

const isAttachmentTypeCategory = (
  obj: Partial<AttachmentTypeCategoryDto | unknown>,
): obj is AttachmentTypeCategoryDto => !!(obj as AttachmentTypeCategoryDto)?.attachmentTypeCategoryId;

export const isResume = (obj: Partial<ResumeDto | unknown>): obj is ResumeDto => !!(obj as ResumeDto)?.resumeId;

const isImage = (obj: Partial<ImageDto | unknown>): obj is ImageDto => !!(obj as ImageDto)?.imageId;

export const getFileType = <T extends BaseAttachmentDto>(file: T): BaseFileTypeDto => {
  if (isPrivateFile(file)) {
    return file.privateFileType;
  }

  if (isCandidateFile(file)) {
    return file.attachmentType;
  }

  return undefined;
};

export const getFileId = <T extends BaseAttachmentDto>(file: T) => {
  if (isPrivateFile(file)) {
    return file.privateFileId;
  }
  if (isCandidateFile(file)) {
    return file.attachmentId;
  }
  if (isResume(file)) {
    return file.resumeId;
  }
  if (isImage(file)) {
    return file.imageId;
  }
  return undefined;
};

export const getFileName = <T extends BaseAttachmentDto>(file: T) => file?.title || file?.filename || '';

export const getFileLabel = <T extends BaseAttachmentDto>(file: T) => getFileName(file);

export const getFileTypeCode = <T extends BaseFileTypeDto>(fileType: T) => fileType?.code;

export const getFileTypeName = <T extends BaseFileTypeDto>(fileType: T) => fileType?.name;

export const getFileTypeCodeOfFile = <T extends BaseAttachmentDto>(file: T) => {
  const fileType = getFileType(file);

  return getFileTypeCode(fileType);
};

export const getFileTypeNameOfFile = <T extends BaseAttachmentDto>(file: T) => {
  const fileType = getFileType(file);

  return getFileTypeName(fileType);
};

export const getFileTypeIdOfFile = <T extends BaseAttachmentDto>(file: T) => {
  const fileType = getFileType(file);

  return getFileTypeId(fileType);
};

export const filesSorter = <F extends BaseAttachmentDto>(file1: F, file2: F) => {
  const type1 = getFileType(file1);
  const type2 = getFileType(file2);

  const diff = (type1?.sortOrder || 0) - (type2?.sortOrder || 0);

  if (diff) {
    return diff;
  }

  const date1 = file1.dateCreated && moment(file1.dateCreated).endOf('second');
  const date2 = file2.dateCreated && moment(file2.dateCreated).endOf('second');

  return date1 > date2 ? -1 : 1;
};

export const sortFiles = <T extends BaseAttachmentDto>(files: T[]) => (files ? [...files].sort(filesSorter) : files);

export const sortFileCategories = (fileTypeCategories: BaseFileTypeCategoryDto[]) =>
  fileTypeCategories
    ? [...fileTypeCategories].sort((ftc1, ftc2) => {
        if (!ftc1?.name) {
          return 1;
        }
        if (!ftc2?.name) {
          return -1;
        }
        return ftc1?.name?.localeCompare(ftc2?.name);
      })
    : fileTypeCategories;

export const getFileTypeId = <T extends BaseFileTypeDto>(fileType: T) => {
  if (isPrivateFileType(fileType)) {
    return fileType.privateFileTypeId;
  }

  if (isAttachmentType(fileType)) {
    return fileType.attachmentTypeId;
  }

  return undefined;
};

export const getFileTypeCategoryId = <T extends BaseFileTypeCategoryDto>(category: T) => {
  if (isPrivateFileTypeCategory(category)) {
    return category.privateFileTypeCategoryId;
  }
  if (isAttachmentTypeCategory(category)) {
    return category.attachmentTypeCategoryId;
  }

  return undefined;
};

export const getFileCategory = <F extends BaseAttachmentDto>(file: F) => {
  if (isPrivateFile(file)) {
    return file.privateFileType?.privateFileTypeCategory;
  }
  if (isCandidateFile(file)) {
    return file.attachmentType?.attachmentTypeCategory;
  }
  return undefined;
};

export const getFileTypeCategory = <F extends BaseFileTypeDto>(fileType: F) => {
  if (isPrivateFileType(fileType)) {
    return fileType.privateFileTypeCategory;
  }
  if (isAttachmentType(fileType)) {
    return fileType.attachmentTypeCategory;
  }
  return undefined;
};

export const getCategoryIdOfFile = <F extends BaseAttachmentDto>(file: F) => {
  const category = getFileCategory(file);

  return getFileTypeCategoryId(category);
};

export const prepareFilesData = <F extends BaseAttachmentDto, FT extends BaseFileTypeDto>(
  files: F[],
  fileTypes: FT[],
) => {
  const categoriesCache = fileTypes?.reduce((acc, ft) => {
    if (isPrivateFileType(ft)) {
      acc[ft.privateFileTypeId] = ft.privateFileTypeCategory;
    }
    if (isAttachmentType(ft)) {
      acc[ft.attachmentTypeId] = ft.attachmentTypeCategory;
    }
    return acc;
  }, {});

  const filesWithCategory = files.map((file) => {
    if (isPrivateFile(file)) {
      return {
        ...file,
        privateFileType: {
          ...(file.privateFileType || {}),
          privateFileTypeCategory: categoriesCache[file.privateFileType?.privateFileTypeId],
        },
      };
    }
    if (isCandidateFile(file)) {
      return {
        ...file,
        attachmentType: {
          ...(file.attachmentType || {}),
          attachmentTypeCategory: categoriesCache[file.attachmentType?.attachmentTypeId],
        },
      };
    }
    return file;
  });

  return [filesWithCategory, fileTypes, uniqBy(Object.values(categoriesCache), getFileTypeCategoryId)] as const;
};

export const formatCreated = <T extends BaseAttachmentDto>(file: T, dateFormat: string) => {
  const createdAt = file.createdAt || file.dateCreated;
  const createdBy = file?.creator?.name?.displayName || '';
  const datePart = createdAt ? (momentify(createdAt) as Moment).format(dateFormat) : '';
  const creatorPart = createdBy ? `by ${createdBy}` : '';
  return `${datePart} ${creatorPart}`;
};

export const fileExpired = (file: BaseAttachmentDto) =>
  file.expiryDate && moment(file.expiryDate).endOf('day') < moment();

export const fileSuitable = <F extends BaseAttachmentDto>(file: F, filter: FilesFilterValues) => {
  const isFileExpired = fileExpired(file);

  if (filter.expired != null && filter.expired !== isFileExpired) {
    return false;
  }

  if (filter.verified != null && filter.verified !== file.verified) {
    return false;
  }

  if (filter.fileNamePattern) {
    const fileNameLower = getFileName(file).toLowerCase();
    const fileNamePatternLower = filter.fileNamePattern.toLowerCase();

    if (!fileNameLower.includes(fileNamePatternLower) && !fileNamePatternLower.includes(fileNameLower)) {
      return false;
    }
  }

  if (filter.fileTypes?.length) {
    const fileTypeIds = filter.fileTypes.map((ft) => ft.value);
    const index = fileTypeIds.findIndex((fileTypeId) => fileTypeId === getFileTypeIdOfFile(file));
    if (index < 0) {
      return false;
    }
  }

  return true;
};

export const getStoredFilesFilter = (fileKind: string, entityId: number) =>
  JSON.parse(localStorage.getItem(`${fileKind}-${entityId}-filters`)) || {};

export const storeFilesFilter = (fileKind: string, entityId: number, values: FilesFilterValues) => {
  localStorage.setItem(`${fileKind}-${entityId}-filters`, JSON.stringify(values));
};

export const removeFilesFilter = (fileKind: string, entityId: number) => {
  localStorage.removeItem(`${fileKind}-${entityId}-filters`);
};

export const normalizeFilesFilter = (values: FilesFilterValues) => {
  const valuesToSet: FilesFilterValues = {};

  if (values.fileNamePattern) {
    valuesToSet.fileNamePattern = values.fileNamePattern;
  }

  if (!isEmpty(values.fileTypes)) {
    valuesToSet.fileTypes = values.fileTypes;
  }

  if (values.expired) {
    valuesToSet.expired = values.expired;
  }

  if (values.verified != null) {
    valuesToSet.verified = values.verified;
  }

  return valuesToSet;
};

export const getFileUploadInitialValues = <FT extends BaseFileTypeDto>(fileTypes: FT[]) => {
  const noType = fileTypes.find((ft) => ft.code === attachmentTypeCodes.noType);

  return {
    fileType: noType ? buildOptionFromFileType(noType) : null,
  };
};

export const getOperationPermissionChecker =
  <FV, F extends BaseAttachmentDto>(
    operation: ValuesOf<FileOperations<FV, F>>,
    isAllowedOperation: IsAllowedOperation,
    userGroupIds: number[],
  ) =>
  (file?: F) => {
    if (!operation) {
      return false;
    }

    if (operation.isAllowed == null) {
      return true;
    }

    if (typeof operation.isAllowed !== 'function') {
      return !!operation.isAllowed;
    }

    return operation.isAllowed(isAllowedOperation, file, userGroupIds);
  };

export const getFileKindControls = <
  F extends BaseAttachmentDto,
  FT extends BaseFileTypeDto,
  FC extends BaseFileTypeCategoryDto = BaseFileTypeCategoryDto,
  FV extends UploadFileBaseValues = FormUploadFileValues,
>(
  operations: FileOperations<FV, F>,
  fileTypes: FT[],
  userGroupIds: number[],
  obtainUrl: (file: Partial<F>) => Promise<string>,
  parseFile: (file: F) => Promise<unknown>,
  updateFile: (file: F, values: FormUploadFileValues) => Promise<unknown>,
  deleteFile: (file: F) => Promise<unknown>,
  isAllowedOperation: IsAllowedOperation,
  formValues: FormValuesType,
  readOnly?: boolean,
) =>
  [
    {
      actionId: 'download',
      action: () => Promise.resolve(true),
      render: (renderProps: ControlRenderProps<F, FC>) => (
        <FileButtonDownload file={renderProps.entity} onObtainUrl={obtainUrl} />
      ),
      showCondition: getOperationPermissionChecker(operations.obtainUrl, isAllowedOperation, userGroupIds),
    },
    !readOnly
      ? {
          actionId: 'parse',
          action: parseFile,
          render: (renderProps: ControlRenderProps<F, FC>) => (
            <FileButtonParse
              file={renderProps.entity}
              formValues={formValues}
              onSubmit={(file) =>
                renderProps
                  .action(file)
                  .then((res) => {
                    renderProps.updateRowState({ error: null });
                    return res;
                  })
                  .catch((err) => {
                    renderProps.updateRowState({ error: stringifyError(err) });
                  })
              }
            />
          ),
          showCondition: getOperationPermissionChecker(operations.parse, isAllowedOperation, userGroupIds),
        }
      : null,
    !readOnly
      ? {
          actionId: 'update',
          action: updateFile,
          render: (renderProps: ControlRenderProps<F, FC>) => (
            <FileButtonEditFile file={renderProps.entity} fileTypes={fileTypes} onSubmit={renderProps.action} />
          ),
          showCondition: getOperationPermissionChecker(operations.update, isAllowedOperation, userGroupIds),
        }
      : null,
    !readOnly
      ? {
          actionId: 'delete',
          action: deleteFile,
          render: getDeleteEntityControlRenderer(),
          showCondition: getOperationPermissionChecker(operations.delete, isAllowedOperation, userGroupIds),
        }
      : null,
  ].filter(Boolean);

export const mixinFileType = <F extends BaseAttachmentDto, FT extends BaseFileTypeDto>(file: F, fileTypes: FT[]) => {
  const fileTypeIdOfFile = getFileTypeIdOfFile(file);

  const fileType = fileTypes.find((ft) => getFileTypeId(ft) === fileTypeIdOfFile);

  if (isPrivateFile(file)) {
    return {
      ...file,
      privateFileType: fileType,
    };
  }

  if (isCandidateFile(file)) {
    return {
      ...file,
      attachmentType: fileType,
    };
  }

  return file;
};
